import React, { useEffect, useState } from "react";
import SectionHeader from "../components/SectionHeader";
import { getTripSheets, updateTripSheet } from "../services/tripSheetService";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Search, CheckCircle, Clock, ChevronDown, ChevronRight } from "lucide-react";
import CustomDropdown from "../components/CustomDropdown";
import { routeOptions } from "../utils/helper";

const TripsheetIn = () => {
    const { user } = useSelector((state) => state.user);

    const [isLoading, setIsLoading] = useState(false);
    const [tripsheetList, setTripSheetList] = useState([]);

    const [searchTripSheet, setSearchTripSheet] = useState("");
    const [searchRoute, setSearchRoute] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
    const [expandedRowIds, setExpandedRowIds] = useState([]);

    const toggleRow = (id) => {
        setExpandedRowIds(prev =>
            prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
        );
    };

    const handleGetTripSheetList = async () => {
        try {
            setIsLoading(true);
            const branchQuery = user?.role === "EMPLOYEE" ? (user?.branch?.code || "") : "";
            const response = await getTripSheets(branchQuery);
            if (response?.status === 200) {
                const initializedTripSheets = (response?.data || []).map(t => ({
                    ...t,
                    status: t.status || 'PENDING'
                }));
                setTripSheetList(initializedTripSheets);
            }
        } catch (error) {
            console.error("Error fetching tripsheet list:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleGetTripSheetList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusChange = async (tripSheet, newStatus) => {
        // Optimistic UI update
        setTripSheetList((prev) =>
            prev.map((t) => (t._id === tripSheet._id ? { ...t, status: newStatus } : t))
        );

        try {
            const updatedData = { ...tripSheet, status: newStatus };
            const res = await updateTripSheet(tripSheet._id, updatedData);
            if (res.status === 200) {
                toast.success(`TripSheet status updated to ${newStatus}`);
            } else {
                // Rollback on failure
                setTripSheetList((prev) =>
                    prev.map((t) => (t._id === tripSheet._id ? { ...t, status: tripSheet.status } : t))
                );
                toast.error("Failed to update status");
            }
        } catch (error) {
            // Rollback on failure
            setTripSheetList((prev) =>
                prev.map((t) => (t._id === tripSheet._id ? { ...t, status: tripSheet.status } : t))
            );
            toast.error(error?.response?.data?.error || "Error updating TripSheet");
            console.error("Error updating TripSheet:", error);
        }
    };

    const goToPrevDate = () => {
        const currentDate = new Date(selectedDate);
        currentDate.setDate(currentDate.getDate() - 1);
        setSelectedDate(currentDate.toISOString().split("T")[0]);
    };

    const goToNextDate = () => {
        const currentDate = new Date(selectedDate);
        currentDate.setDate(currentDate.getDate() + 1);
        setSelectedDate(currentDate.toISOString().split("T")[0]);
    };

    // Local filtering based on all fields
    const filteredTripSheets = tripsheetList.filter((tripSheet) => {
        const tripSheetNo = tripSheet?.tripSheetNo || "";
        const routeName = tripSheet?.routeName || "";
        const tripDate = tripSheet?.tripDate || "";

        const matchesTripSheet = tripSheetNo.toLowerCase().includes(searchTripSheet.toLowerCase());

        // Explicit exact matching against CustomDropdown for routing
        const matchesRoute = searchRoute ? routeName.toLowerCase() === searchRoute.toLowerCase() : true;

        // Check if the tripSheet's date matches the selected Date
        const matchesDate = selectedDate ? tripDate.includes(selectedDate) : true;

        return matchesTripSheet && matchesRoute && matchesDate;
    });

    return (
        <div className="p-3 bg-slate-200 min-h-full">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
                <SectionHeader
                    title="TripSheet In"
                    subTitle="Update arrival status for incoming tripsheets"
                />
                <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">

                    {/* Date Picker */}
                    <div className="flex items-center gap-1 bg-white border border-gray-300 rounded px-2 py-1.5 h-[38px] text-sm">
                        <button onClick={goToPrevDate} className="px-2 font-bold text-gray-500 hover:text-black">‹</button>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="flex-1 px-1 bg-transparent outline-none cursor-pointer"
                        />
                        <button onClick={goToNextDate} className="px-2 font-bold text-gray-500 hover:text-black">›</button>
                    </div>

                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 border border-gray-300 rounded h-[38px]">
                        <Search size={16} className="text-gray-400" />
                        <input
                            type="text"
                            placeholder="TripSheet No..."
                            value={searchTripSheet}
                            onChange={(e) => setSearchTripSheet(e.target.value)}
                            className="px-2 text-sm bg-transparent outline-none w-32"
                        />
                    </div>

                    {/* Route Dropdown mapped to Helper Options */}
                    <div className="w-64 z-50">
                        <CustomDropdown
                            placeholder="Select Route..."
                            value={searchRoute}
                            options={routeOptions}
                            onChange={(val) => setSearchRoute(val)}
                        />
                    </div>

                    {/* Clear Route Button */}
                    {searchRoute && (
                        <button
                            onClick={() => setSearchRoute("")}
                            className="px-3 py-2 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 h-[38px]"
                        >
                            Clear
                        </button>
                    )}

                </div>
            </div>

            {/* Table Section */}
            <div className="bg-white border rounded-xl overflow-hidden mt-4">
                {isLoading ? (
                    <div className="p-10 text-center text-gray-500">Loading tripsheets...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full border">
                            <thead className="bg-gray-100 text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="px-4 py-3 text-center">TripSheet No</th>
                                    <th className="px-4 py-3 border-l text-center">Trip Date</th>
                                    <th className="px-4 py-3 border-l text-center">Route Name</th>
                                    <th className="px-4 py-3 border-l text-center">Vendor Name</th>
                                    <th className="px-4 py-3 border-l text-center">Status</th>
                                    <th className="px-4 py-3 border-l text-center">Update Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTripSheets?.map((tripSheet) => (
                                    <React.Fragment key={tripSheet._id}>
                                        <tr className="border-t text-sm hover:bg-slate-50 transition-colors" >
                                            <td className="px-4 py-3 border-l font-medium text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button onClick={() => toggleRow(tripSheet._id)} className="p-1 hover:bg-gray-200 rounded text-gray-600 transition-colors">
                                                        {expandedRowIds.includes(tripSheet._id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                    </button>
                                                    {tripSheet?.tripSheetNo}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 border-l text-center">
                                                {tripSheet?.tripDate}
                                            </td>
                                            <td className="px-4 py-3 border-l text-center">
                                                {tripSheet?.routeName}
                                            </td>
                                            <td className="px-4 py-3 border-l text-center">
                                                {tripSheet?.vendorName}
                                            </td>
                                            <td className="px-4 py-3 border-l text-center">
                                                {tripSheet?.status === "ARRIVED" ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                                                        <CheckCircle className="w-3.5 h-3.5" />
                                                        {tripSheet?.status}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                                                        <Clock className="w-3.5 h-3.5" />
                                                        {tripSheet?.status}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-l text-center">
                                                <select
                                                    value={tripSheet?.status}
                                                    onChange={(e) => handleStatusChange(tripSheet, e.target.value)}
                                                    className="bg-white border border-gray-300 text-gray-700 text-xs rounded focus:ring-sky-500 focus:border-sky-500 p-1 w-24 cursor-pointer outline-none"
                                                >
                                                    <option value="PENDING">PENDING</option>
                                                    <option value="ARRIVED">ARRIVED</option>
                                                </select>
                                            </td>
                                        </tr>
                                        {expandedRowIds.includes(tripSheet._id) && (
                                            <tr className="bg-slate-50 border-b shadow-inner">
                                                <td colSpan="6" className="px-8 py-4">
                                                    <div className="border border-gray-200 bg-white rounded-lg shadow-sm overflow-hidden">
                                                        <table className="w-full text-xs box-border">
                                                            <thead className="bg-slate-100 text-gray-500 uppercase">
                                                                <tr>
                                                                    <th className="px-4 py-2 font-semibold text-center">Manifest No</th>
                                                                    <th className="px-4 py-2 border-l font-semibold text-center">Start Location</th>
                                                                    <th className="px-4 py-2 border-l font-semibold text-center">End Location</th>
                                                                    <th className="px-4 py-2 border-l font-semibold text-center">Total Pkg</th>
                                                                    <th className="px-4 py-2 border-l font-semibold text-center">Total Weight</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tripSheet?.manifests?.map((mani, mIdx) => (
                                                                    <tr key={mIdx} className="border-t border-gray-100 hover:bg-sky-50 transition-colors">
                                                                        <td className="px-4 py-2 text-center font-medium text-gray-700">{mani.manifestNo}</td>
                                                                        <td className="px-4 py-2 border-l text-center text-gray-700">{mani.startLocation}</td>
                                                                        <td className="px-4 py-2 border-l text-center text-gray-700">{mani.endLocation}</td>
                                                                        <td className="px-4 py-2 border-l text-center text-gray-700">{mani.totalPkg}</td>
                                                                        <td className="px-4 py-2 border-l text-center text-gray-700">{mani.totalWeight}</td>
                                                                    </tr>
                                                                ))}
                                                                {(!tripSheet?.manifests || tripSheet.manifests.length === 0) && (
                                                                    <tr>
                                                                        <td colSpan="5" className="p-4 text-center text-gray-500 italic">No manifests bound to this tripsheet.</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {filteredTripSheets?.length === 0 && (
                                    <tr className="h-64">
                                        <td colSpan="6" className="py-6 text-center text-gray-500">
                                            No tripsheets found for {selectedDate}.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TripsheetIn;
